// ── DIG DEEP — Main Entry Point ──

import { Game } from './game/Game';
import { Renderer } from './ui/Renderer';
import { UIManager } from './ui/UIManager';
import { ScreenManager } from './ui/Screens';
import { Animations } from './ui/Animations';
import { Tutorial } from './ui/Tutorial';
import { audioManager } from './audio/AudioManager';
import { saveManager } from './save/SaveManager';

class DigDeepApp {
  private game: Game;
  private renderer!: Renderer;
  private ui!: UIManager;
  private screens!: ScreenManager;
  private animations!: Animations;
  private tutorial!: Tutorial;
  private animFrameId: number = 0;
  private autoSaveInterval: number = 0;

  constructor() {
    this.game = new Game();
  }

  init(): void {
    const container = document.getElementById('game-container');
    if (!container) {
      console.error('Game container not found');
      return;
    }

    // Create canvas for the dig grid
    const canvas = document.createElement('canvas');
    canvas.id = 'dig-canvas';
    canvas.className = 'dd-canvas';

    // Initialize renderer
    this.renderer = new Renderer(canvas);

    // Initialize screen manager
    this.screens = new ScreenManager(container);

    // Initialize animations
    this.animations = new Animations(container);

    // Initialize UI
    this.ui = new UIManager(this.game, this.renderer, container);
    this.ui.init();

    // Initialize tutorial
    this.tutorial = new Tutorial(this.game);

    // Wire up game events
    this.game.onEvent = (event) => {
      this.ui.handleGameEvent(event);

      // Tutorial event forwarding
      if (this.tutorial.active) {
        this.tutorial.checkEvent(event.type);
      }

      // Animations for specific events
      switch (event.type) {
        case 'artifact_found':
          audioManager.playDiscovery(event.data?.rarity || 'common');
          break;
        case 'weather_change':
          audioManager.playWeatherChange(event.data?.weather || 'clear');
          break;
        case 'sale':
          audioManager.playSell();
          if (event.data?.price) {
            const rect = container.getBoundingClientRect();
            this.animations.floatingText(
              rect.width / 2, rect.height / 2,
              `+${formatMoneySimple(event.data.price)}`,
              '#4CAF50'
            );
          }
          break;
        case 'theory_result':
          if (event.data?.correct) {
            audioManager.playTheoryCorrect();
          } else {
            audioManager.playTheoryWrong();
          }
          break;
        case 'day_end':
          audioManager.playDayEnd();
          break;
        case 'story_fragment':
          audioManager.playStoryReveal();
          break;
        case 'media_deal':
          if (event.data?.amount) {
            const rect = container.getBoundingClientRect();
            this.animations.floatingText(
              rect.width / 2, rect.height / 3,
              `Media Deal! +${formatMoneySimple(event.data.amount)}`,
              '#D4A03C'
            );
          }
          break;
      }
    };

    // Initialize audio on first user interaction
    const initAudio = () => {
      audioManager.init();
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
    document.addEventListener('click', initAudio);
    document.addEventListener('touchstart', initAudio);

    // Auto-save every 60 seconds
    this.autoSaveInterval = window.setInterval(() => {
      if (this.game.currentScreen !== 'menu') {
        try {
          saveManager.autoSave(this.game.exportSave());
        } catch (e) {
          console.warn('Auto-save failed:', e);
        }
      }
    }, 60000);

    // Handle window resize
    window.addEventListener('resize', () => {
      this.renderer.resize();
    });

    // Start render loop
    this.startRenderLoop();

    // Show menu screen
    this.game.setScreen('menu');
    this.ui.showScreen('menu');

    console.log('DIG DEEP initialized');
  }

  private startRenderLoop(): void {
    const loop = () => {
      if (this.game.digSite && this.game.currentScreen === 'dig') {
        // Self-healing resize: if canvas dimensions don't match parent, fix them
        const parent = this.renderer.canvas.parentElement;
        if (parent) {
          const pw = parent.clientWidth;
          const ph = parent.clientHeight;
          if (pw > 0 && ph > 0 && (this.renderer.canvas.width !== pw || this.renderer.canvas.height !== ph)) {
            this.renderer.resize();
          }
        }
        this.renderer.render(this.game.digSite.grid);
      }
      this.animFrameId = requestAnimationFrame(loop);
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  destroy(): void {
    cancelAnimationFrame(this.animFrameId);
    clearInterval(this.autoSaveInterval);
    this.animations.destroy();
    audioManager.stopAmbient();
  }
}

function formatMoneySimple(amount: number): string {
  return '$' + amount.toLocaleString('en-US');
}

// ── Bootstrap ──
window.addEventListener('DOMContentLoaded', () => {
  const app = new DigDeepApp();
  app.init();
});
